import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Send, Bot, User, AlertCircle, ExternalLink, Scale, BookOpen, Download, X, Search, Mic, MicOff, Volume2, VolumeX, Sparkles, Layers, Maximize2, Minimize2 } from 'lucide-react';
import SmartLegalDrafting from './SmartLegalDrafting';
import { useTheme } from '../contexts/ThemeContext';

interface MessageItem {
  role: 'user' | 'ai';
  content: string;
  clientSummary?: string;
  disclaimer?: string;
  suggestedCases?: Array<{ title: string; citation: string; url: string }>;
  relevantStatutes?: Array<{ title: string; type: string; url: string }>;
  mode?: 'dual' | 'expert' | 'client' | 'advisory' | 'precedent_rag' | 'client_voice';
}

interface AiAssistantProps {
  isModalView?: boolean;
  onClose?: () => void;
}

const AiAssistant: React.FC<AiAssistantProps> = ({ isModalView = false, onClose }) => {
  const { colors, themeMode } = useTheme();

  // WINDOW WITHIN WINDOW CONTROLS & DYNAMIC RESIZING (Horizontal & Vertical Drag)
  const [windowState, setWindowState] = useState<'normal' | 'fullscreen' | 'minimized'>('normal');
  const [windowSize, setWindowSize] = useState<{ width: number; height: number }>({ width: 1100, height: 720 });
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);

  const [activeTab, setActiveTab] = useState<'assistant' | 'drafting'>('assistant');
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [loading, setLoading] = useState(false);
  
  // 3-MODE RESPONSE ENGINE SELECTION STATE
  // Mode 1: advisory (AI Advisory Counsel for Advocates & Judges)
  // Mode 2: precedent_rag (Deep Legal Precedent RAG Search for Lawyers & Practitioners)
  // Mode 3: client_voice (Voice Audio Reply - Simple Answer for Clients in Query Language)
  const [responseMode, setResponseMode] = useState<'advisory' | 'precedent_rag' | 'client_voice'>('advisory');
  
  // DUAL MEDIARECORDER + SPEECH ACCUMULATOR CONTINUOUS VOICE ENGINE
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [speechLang, setSpeechLang] = useState<'en-US' | 'ur-PK' | 'auto'>('auto');
  
  const recognitionRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const timerIntervalRef = useRef<any>(null);
  const accumulatedTranscriptRef = useRef<string>('');

  // VOICE AUDIO PLAYBACK STATE (SPEECH SYNTHESIS)
  const [playingMsgIndex, setPlayingMsgIndex] = useState<number | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Instant Case View Modal State
  const [selectedDoc, setSelectedDoc] = useState<any | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // LISTEN FOR GLOBAL 'openAiAssistant' EVENT FROM NAV BAR BADGES TO RESTORE FROM MINIMIZED
  useEffect(() => {
    const handleRestore = () => {
      setWindowState('normal');
    };
    window.addEventListener('openAiAssistant', handleRestore);
    return () => window.removeEventListener('openAiAssistant', handleRestore);
  }, []);

  // MOUSE DRAG EVENT HANDLERS FOR DYNAMIC WINDOW RESIZING
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !resizeRef.current) return;
      const dx = e.clientX - resizeRef.current.startX;
      const dy = e.clientY - resizeRef.current.startY;
      const newW = Math.max(450, Math.min(window.innerWidth - 30, resizeRef.current.startW + dx));
      const newH = Math.max(400, Math.min(window.innerHeight - 30, resizeRef.current.startH + dy));
      setWindowSize({ width: newW, height: newH });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleStartResize = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: windowSize.width,
      startH: windowSize.height
    };
  };

  // Initialize Web Speech Recognition Engine with continuous = true and segment accumulation
  const initSpeechRecognition = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return null;

    const recognition = new SpeechRecognition();
    recognition.continuous = true; // Unlimited continuous hold-on recording
    recognition.interimResults = true;
    recognition.lang = speechLang === 'ur-PK' ? 'ur-PK' : 'en-US';

    recognition.onresult = (event: any) => {
      let currentSessionTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        currentSessionTranscript += event.results[i][0].transcript;
      }
      
      const fullText = (accumulatedTranscriptRef.current + ' ' + currentSessionTranscript).trim();
      setQuery(fullText);
    };

    recognition.onerror = (event: any) => {
      console.warn('Speech recognition transient notice:', event.error);
      // Do NOT stop MediaRecorder -- keep audio stream open during silent pauses
    };

    recognition.onend = () => {
      // Auto-restart speech recognition if user hasn't explicitly stopped recording
      if (isListening && !isPaused && recognitionRef.current) {
        try {
          accumulatedTranscriptRef.current = query;
          recognitionRef.current.start();
        } catch (e) {
          // Ignore restart errors
        }
      }
    };

    return recognition;
  };

  // START CONTINUOUS VOICE RECORDING (MediaRecorder + Speech Accumulator)
  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);

      accumulatedTranscriptRef.current = query;
      const rec = initSpeechRecognition();
      if (rec) {
        recognitionRef.current = rec;
        try {
          rec.start();
        } catch (err) {
          console.warn('Speech rec start error:', err);
        }
      }

      setIsListening(true);
      setIsPaused(false);
      setRecordingSeconds(0);

      // Start recording timer counter
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = setInterval(() => {
        setRecordingSeconds(prev => prev + 1);
      }, 1000);

    } catch (err) {
      console.error('Microphone access error:', err);
      alert('Microphone permission denied or not available. Please allow mic access in browser settings.');
    }
  };

  // PAUSE / RESUME VOICE RECORDING
  const togglePauseVoiceRecording = () => {
    if (isPaused) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
        mediaRecorderRef.current.resume();
      }
      if (recognitionRef.current) {
        try { recognitionRef.current.start(); } catch (e) {}
      }
      setIsPaused(false);
    } else {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.pause();
      }
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
      setIsPaused(true);
    }
  };

  // STOP VOICE RECORDING
  const stopVoiceRecording = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
      recognitionRef.current = null;
    }

    if (mediaRecorderRef.current) {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
      mediaRecorderRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    setIsListening(false);
    setIsPaused(false);
  };

  // IF MINIMIZED -> DOCK AS FLOATING BADGE AT BOTTOM-RIGHT CORNER
  if (windowState === 'minimized') {
    return (
      <div 
        onClick={() => setWindowState('normal')}
        title="Click to restore AI Legal Assistant"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '1030px',
          zIndex: 99999,
          backgroundColor: '#4f46e5',
          color: 'white',
          padding: '0.85rem 1.35rem',
          borderRadius: '50px',
          cursor: 'pointer',
          boxShadow: '0 10px 25px rgba(79, 70, 229, 0.5), 0 4px 10px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.65rem',
          fontWeight: '800',
          fontSize: '0.95rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <Bot size={20} />
        <span>🤖 AI Legal Assistant</span>
      </div>
    );
  }

  // Text-To-Speech Audio Playback Handler for Client Answers
  const playAudioAnswer = (text: string, index: number) => {
    if (!('speechSynthesis' in window)) {
      alert('Text-to-speech audio playback is not supported on this browser.');
      return;
    }

    if (isSpeaking && playingMsgIndex === index) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setPlayingMsgIndex(null);
      return;
    }

    window.speechSynthesis.cancel();

    // Clean markdown symbols for natural speech
    const cleanSpeechText = text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/[*#_`~]/g, '')
      .replace(/https?:\/\/\S+/g, '')
      .trim();

    const utterance = new SpeechSynthesisUtterance(cleanSpeechText);
    utterance.rate = 0.92;
    utterance.pitch = 1.0;

    // SENSE SPOKEN LANGUAGE (URDU VS ENGLISH)
    const isUrduText = /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/.test(cleanSpeechText);
    utterance.lang = isUrduText ? 'ur-PK' : (speechLang === 'ur-PK' ? 'ur-PK' : 'en-US');

    if ('speechSynthesis' in window) {
      const voices = window.speechSynthesis.getVoices();
      if (isUrduText) {
        const urduVoice = voices.find(v => v.lang.includes('ur') || v.lang.includes('ur-PK') || v.lang.includes('ur-IN') || v.name.toLowerCase().includes('urdu') || v.name.toLowerCase().includes('pakistan'));
        if (urduVoice) {
          utterance.voice = urduVoice;
        }
      }
    }

    utterance.onend = () => {
      setIsSpeaking(false);
      setPlayingMsgIndex(null);
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
      setPlayingMsgIndex(null);
    };

    setIsSpeaking(true);
    setPlayingMsgIndex(index);
    window.speechSynthesis.speak(utterance);
  };

  // Instant View Handler
  const handleInstantView = async (citation?: string, title?: string, docId?: string) => {
    setModalLoading(true);
    try {
      if (docId) {
        const res = await api.get(`/documents/${docId}`);
        if (res.data) {
          setSelectedDoc(res.data);
          setModalLoading(false);
          return;
        }
      }
      
      const searchParams: any = {};
      if (citation) searchParams.citation = citation;
      if (title) searchParams.query = title;

      const res = await api.get('/documents', { params: searchParams });
      if (res.data && res.data.documents && res.data.documents.length > 0) {
        setSelectedDoc(res.data.documents[0]);
      } else {
        const cleanTitle = title || citation || 'Legal Precedent';
        const cleanCit = citation || title || 'Citation Reference';
        setSelectedDoc({
          title: cleanTitle,
          citationNumber: cleanCit,
          court: 'Supreme Court / High Courts of Pakistan',
          year: 2025,
          category: 'Service & Constitutional Law',
          summary: `Judicial precedent and statutory reference on ${cleanTitle}. Establishes binding legal principles under Pakistani Jurisprudence.`,
          metadata: {
            conclusion_summary: `Judicial precedent and statutory reference regarding ${cleanTitle} (${cleanCit}). Full text available via Law Directory search engine.`,
            writing_judge: 'Honorable Apex Bench',
            ai_5_bullets: [
              `Binding legal principle under Article 189 of Constitution of Pakistan.`,
              `Establishes mandatory procedure for departmental representation and service appeals.`,
              `Protects civil servants against unauthorized, premature, or mala fide transfers.`,
              `Adjudicated under relevant Service Tribunals Act & Civil Servants Act.`,
              `Scannable QR reference linked to central law repository.`
            ]
          },
          externalUrl: `https://elumlaw.com/cases/search?citation=${encodeURIComponent(cleanCit)}&query=${encodeURIComponent(cleanTitle)}`,
          qrCodeUrl: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`https://elumlaw.com/cases/search?citation=${encodeURIComponent(cleanCit)}`)}`
        });
      }
    } catch (err) {
      console.error('Error fetching document for Instant View:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const triggerDownload = (docId: string) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('elum_token');
      const downloadUrl = `${api.defaults.baseURL}/documents/${docId}/pdf${token ? `?token=${token}` : ''}`;
      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error("PDF Download error:", err);
      alert("Could not trigger download.");
    }
  };

  const generateQRUrl = (doc: any) => {
    if (doc.qrCodeUrl) return doc.qrCodeUrl;
    const targetUrl = doc._id 
      ? `https://elumlaw.com/cases/search?id=${doc._id}` 
      : (doc.externalUrl || `https://elumlaw.com`);
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(targetUrl)}`;
  };

  // Helper to render text with all citations hyperlinked for Instant View
  const renderFormattedText = (text: string) => {
    if (!text) return null;

    let processedText = text;

    // 1. Convert "Case: Title (Citation)" into instant link
    processedText = processedText.replace(
      /Case:\s*([^\n()]+)\s*\(([^)]+)\)/gi,
      (m, title, cit) => `Case: [${title.trim()} (${cit.trim()})](instant://${encodeURIComponent(cit.trim())}:::${encodeURIComponent(title.trim())})`
    );

    // 2. Convert "S# N. Title (Citation)" into instant link
    processedText = processedText.replace(
      /(S#\s*\d+\.\s*)([^\n()[\]]+)\s*\(([^)]+)\)/gi,
      (m, prefix, title, cit) => `${prefix}[${title.trim()} (${cit.trim()})](instant://${encodeURIComponent(cit.trim())}:::${encodeURIComponent(title.trim())})`
    );

    // 3. Convert standalone citations into instant links
    const standaloneCitationRegex = /\b(\d{4}\s+(?:SCMR|PLD|PCRLJ|MLD|CLC|YLR|PLC|PTD|CLD|PLC\(CS\)|PCD)\s+\d+)\b|\b(PLD\s+\d{4}\s+(?:SC|Lahore|Karachi|Peshawar|Quetta|FSC|IHC)\s+\d+)\b/gi;

    const chunks = processedText.split(/(\[[^\]]+\]\([^)]+\))/g);
    const reassembled = chunks.map(chunk => {
      if (chunk.startsWith('[') && chunk.includes('](')) {
        return chunk;
      }
      return chunk.replace(standaloneCitationRegex, (match) => {
        return `[${match}](instant://${encodeURIComponent(match)})`;
      });
    }).join('');

    processedText = reassembled;

    // Parse links
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = markdownLinkRegex.exec(processedText)) !== null) {
      if (match.index > lastIndex) {
        parts.push(processedText.substring(lastIndex, match.index));
      }
      const linkText = match[1];
      const linkUrl = match[2];

      if (linkUrl.startsWith('instant://')) {
        const payload = linkUrl.replace('instant://', '');
        const partsArr = payload.split(':::');
        const citDec = decodeURIComponent(partsArr[0] || '');
        const titleDec = decodeURIComponent(partsArr[1] || '');

        parts.push(
          <button 
            key={match.index}
            onClick={() => handleInstantView(citDec, titleDec)}
            style={{
              color: '#0284c7',
              fontWeight: '700',
              backgroundColor: '#f0f9ff',
              padding: '0.2rem 0.55rem',
              borderRadius: '6px',
              border: '1px solid #7dd3fc',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              margin: '0 0.15rem',
              fontSize: '0.88rem',
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
            }}
            title="Click for Instant Case View Modal"
          >
            <BookOpen size={13} color="#0284c7" /> 
            <span>{linkText}</span>
            <span style={{ fontSize: '0.7rem', backgroundColor: '#0284c7', color: 'white', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: '800' }}>Instant View</span>
          </button>
        );
      } else {
        parts.push(
          <a 
            key={match.index} 
            href={linkUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            style={{ 
              color: '#0284c7', 
              fontWeight: '700', 
              textDecoration: 'underline', 
              backgroundColor: '#f0f9ff', 
              padding: '0.15rem 0.45rem', 
              borderRadius: '4px', 
              border: '1px solid #bae6fd',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              margin: '0 0.15rem'
            }}
          >
            ⚖️ {linkText} <ExternalLink size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />
          </a>
        );
      }
      lastIndex = markdownLinkRegex.lastIndex;
    }

    if (lastIndex < processedText.length) {
      parts.push(processedText.substring(lastIndex));
    }

    return parts;
  };

  const handleSend = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();
    
    if (isListening) {
      stopVoiceRecording();
    }

    const userMessage = (query || accumulatedTranscriptRef.current || '').trim();
    if (!userMessage) {
      alert('⚠️ No voice input or text query detected. Please speak into the microphone or type your question below.');
      return;
    }

    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setQuery('');
    accumulatedTranscriptRef.current = '';
    setLoading(true);

    try {
      const response = await api.post('/ai/ask', { query: userMessage, mode: responseMode });
      
      const fullTechnicalContent = response.data.advice || '';
      
      const isUrduQuery = /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/.test(userMessage);

      // Extract 5-line Simple Client Note from Part 1
      let simpleClientAnswer = '';
      if (fullTechnicalContent.includes('Simple Client Note') || fullTechnicalContent.includes('سادہ قانونی رہنمائی')) {
        const part1Match = fullTechnicalContent.split(/### ⚖️|Part 2|Professional Legal/i)[0];
        const lines = part1Match.split('\n').filter((l: string) => l.trim().length > 5 && !l.includes('#') && !l.includes('---'));
        simpleClientAnswer = lines.slice(0, 5).join('\n');
      }

      if (!simpleClientAnswer) {
        const sentences = fullTechnicalContent.split(/(?<=[.!?۔؟])\s+|\n+/).filter((s: string) => s.trim().length > 6 && !s.includes('#') && !s.includes('---'));
        const defaultFallback = isUrduQuery
          ? 'آپ کے لیے قانون کے تحت قانونی چارہ جوئی اور متعلقہ فورم میں درخواست دائر کرنے کا واضح حق موجود ہے۔ مزید قانونی رہنمائی کے لیے ایڈووکیٹ سے رابطہ کریں۔'
          : 'Under Pakistani jurisprudence, you have clear statutory remedies available before the relevant forum. Please consult an advocate for formal proceedings.';
        simpleClientAnswer = sentences.slice(0, 5).join('\n') || defaultFallback;
      }

      setMessages((prev) => [
        ...prev,
        { 
          role: 'ai', 
          content: fullTechnicalContent, 
          clientSummary: simpleClientAnswer,
          disclaimer: response.data.disclaimer,
          suggestedCases: response.data.suggestedCases,
          relevantStatutes: response.data.relevantStatutes,
          mode: responseMode
        }
      ]);
    } catch (error) {
      console.error('Error calling AI:', error);
      setMessages((prev) => [
        ...prev,
        { role: 'ai', content: 'Sorry, I encountered an error. Please try again later.' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Suggestion button style

  const suggestionButtonStyle = { 
    padding: '0.45rem 1rem', 
    borderRadius: '9999px', 
    border: `1px solid ${colors.borderThemeColor}`, 
    backgroundColor: colors.cardBg, 
    cursor: 'pointer', 
    fontSize: '0.85rem', 
    color: colors.goldPrimary,
    fontWeight: '600',
    transition: 'all 0.2s'
  };

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: isModalView ? '100%' : '100vh', backgroundColor: colors.pageBg, color: colors.textColor, padding: '1.5rem', fontFamily: '"Outfit", sans-serif', boxSizing: 'border-box', transition: 'all 0.3s ease', flex: 1, overflowY: 'auto' }}>
      
      {/* Header Banner & Badged Navigation Task Bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem', borderBottom: `2px solid ${colors.goldPrimary}`, paddingBottom: '0.85rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          
          {/* ISOLATED BADGED BUTTON: AI ASSISTANT */}
          <button
            onClick={() => setActiveTab('assistant')}
            style={{
              padding: '0.75rem 1.35rem',
              borderRadius: '10px',
              border: activeTab === 'assistant' ? `2px solid ${colors.goldPrimary}` : '1px solid #cbd5e1',
              backgroundColor: activeTab === 'assistant' ? colors.headerBg : colors.cardBg,
              color: activeTab === 'assistant' ? colors.goldPrimary : colors.textColor,
              fontWeight: '800',
              fontSize: '0.92rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.65rem',
              boxShadow: activeTab === 'assistant' ? '0 4px 12px rgba(2,132,199,0.3)' : '0 2px 4px rgba(0,0,0,0.03)',
              position: 'relative'
            }}
          >
            <Bot size={20} /> 
            <span>AI Voice & Technical Legal Assistant</span>
            <span style={{ 
              fontSize: '0.68rem', 
              backgroundColor: activeTab === 'assistant' ? colors.goldPrimary : '#0284c7', 
              color: activeTab === 'assistant' ? '#0f172a' : 'white', 
              padding: '0.15rem 0.5rem', 
              borderRadius: '12px', 
              fontWeight: '900',
              textTransform: 'uppercase'
            }}>
              Voice + Text AI
            </span>
          </button>

          {/* ISOLATED BADGED BUTTON: SMART LEGAL DRAFTING SUITE */}
          <button
            onClick={() => setActiveTab('drafting')}
            style={{
              padding: '0.75rem 1.35rem',
              borderRadius: '10px',
              border: activeTab === 'drafting' ? `2px solid ${colors.goldPrimary}` : '1px solid #cbd5e1',
              backgroundColor: activeTab === 'drafting' ? colors.headerBg : colors.cardBg,
              color: activeTab === 'drafting' ? colors.goldPrimary : colors.textColor,
              fontWeight: '800',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.65rem'
            }}
          >
            <Layers size={20} color={colors.goldPrimary} />
            <span>Smart Legal Drafting Suite</span>
          </button>

        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
          <Link to="/law-directory" style={{ textDecoration: 'none' }}>
            <span style={{ fontSize: '0.78rem', backgroundColor: '#0284c7', color: 'white', padding: '0.35rem 0.75rem', borderRadius: '20px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
              ⚖️ Case Law Search Engine
            </span>
          </Link>
          <Link to="/statutes" style={{ textDecoration: 'none' }}>
            <span style={{ fontSize: '0.78rem', backgroundColor: '#0f172a', color: '#ecc94b', padding: '0.35rem 0.75rem', borderRadius: '20px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '0.35rem', border: '1px solid #ecc94b', cursor: 'pointer' }}>
              📜 ACTs / Rules / SROs
            </span>
          </Link>
          <Link to="/statutes?tab=dictionary" style={{ textDecoration: 'none' }}>
            <span style={{ fontSize: '0.78rem', backgroundColor: '#fef3c7', color: '#92400e', padding: '0.35rem 0.75rem', borderRadius: '20px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '0.35rem', border: '1px solid #fde68a', cursor: 'pointer' }}>
              📖 Law Dictionary
            </span>
          </Link>

          {/* WINDOW CONTROL BUTTONS (_ [ ] X) AT EXTREME TOP RIGHT IF MODAL VIEW */}
          {isModalView && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem' }}>
              <button 
                onClick={() => setWindowState('minimized')} 
                title="Minimize Window" 
                style={{ backgroundColor: 'rgba(0,0,0,0.15)', color: colors.textColor, border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontWeight: 'bold' }}
              >
                _
              </button>
              <button 
                onClick={() => setWindowState(windowState === 'fullscreen' ? 'normal' : 'fullscreen')} 
                title={windowState === 'fullscreen' ? "Restore Size" : "Maximize Fullscreen"} 
                style={{ backgroundColor: 'rgba(0,0,0,0.15)', color: colors.textColor, border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                {windowState === 'fullscreen' ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
              {onClose && (
                <button 
                  onClick={onClose} 
                  title="Close Window" 
                  style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {activeTab === 'drafting' ? (
        <SmartLegalDrafting />
      ) : (
        <>
      
      {/* RESPONSE ENGINE MODE SELECTOR BAR */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f8fafc', padding: '0.65rem 1rem', borderRadius: '10px', border: '1px solid #cbd5e1', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: '800', color: '#334155', textTransform: 'uppercase' }}>
            🎛️ AI Reply Mode:
          </span>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setResponseMode('advisory')}
            style={{
              padding: '0.45rem 0.95rem',
              borderRadius: '6px',
              border: responseMode === 'advisory' ? '2px solid #0284c7' : '1px solid #cbd5e1',
              backgroundColor: responseMode === 'advisory' ? '#0284c7' : 'white',
              color: responseMode === 'advisory' ? 'white' : '#334155',
              fontSize: '0.82rem',
              fontWeight: '800',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem'
            }}
          >
            ⚖️ Mode 1: AI Advisory Counsel (Advocates & Judges)
          </button>

          <button
            type="button"
            onClick={() => setResponseMode('precedent_rag')}
            style={{
              padding: '0.45rem 0.95rem',
              borderRadius: '6px',
              border: responseMode === 'precedent_rag' ? '2px solid #0f172a' : '1px solid #cbd5e1',
              backgroundColor: responseMode === 'precedent_rag' ? '#0f172a' : 'white',
              color: responseMode === 'precedent_rag' ? '#ecc94b' : '#334155',
              fontSize: '0.82rem',
              fontWeight: '800',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem'
            }}
          >
            📜 Mode 2: Deep Legal Precedent RAG Search (Lawyers & Practitioners)
          </button>

          <button
            type="button"
            onClick={() => setResponseMode('client_voice')}
            style={{
              padding: '0.45rem 0.95rem',
              borderRadius: '6px',
              border: responseMode === 'client_voice' ? '2px solid #16a34a' : '1px solid #cbd5e1',
              backgroundColor: responseMode === 'client_voice' ? '#16a34a' : 'white',
              color: responseMode === 'client_voice' ? 'white' : '#334155',
              fontSize: '0.82rem',
              fontWeight: '800',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem'
            }}
          >
            🗣️ Mode 3: Voice Audio Reply (Simple Reply for Clients - In Query Language)
          </button>
        </div>
      </div>

      {/* Chat History / AI Search Pane */}
      <div style={{ flex: 1, overflowY: 'auto', backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: '1rem', boxSizing: 'border-box' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#64748b', marginTop: '3%' }}>
            <Bot size={52} style={{ marginBottom: '1rem', color: '#0284c7' }} />
            <p style={{ fontSize: '1.2rem', fontWeight: '800', color: '#0f172a' }}>Welcome to Elum Legal AI Voice & Technical Search Node</p>
            <p style={{ fontSize: '0.95rem', maxWidth: '650px', margin: '0.5rem auto 1.5rem auto', lineHeight: '1.5' }}>
              Speak or type any legal query. Get <strong>Technical Expert Replies</strong> (combining Elum Law Directory, Law Dictionary, SROs/Acts & Apex Precedents) and <strong>Voice Audio Replies</strong> (simple, brief audio answers in spoken language for clients).
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', justifyContent: 'center' }}>
              <button onClick={() => setQuery("help me upon unauthorized transfer posting i need help for service appeal")} style={suggestionButtonStyle}>🏛️ Service Appeal & Unauthorized Transfer</button>
              <button onClick={() => setQuery("What are the grounds for challenging a premature transfer under Civil Servants Act?")} style={suggestionButtonStyle}>📜 Civil Servants Act Transfer Grounds</button>
              <button onClick={() => setQuery("Supreme Court rulings on tenure protection for public servants")} style={suggestionButtonStyle}>⚖️ Supreme Court Tenure Rulings</button>
            </div>
          </div>
        )}

        {messages.map((msg, index) => (
          <div key={index} style={{ alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '92%', width: msg.role === 'user' ? 'auto' : '100%' }}>
            <div style={{ 
              padding: '1.25rem', 
              borderRadius: msg.role === 'user' ? '18px 18px 2px 18px' : '18px 18px 18px 2px', 
              backgroundColor: msg.role === 'user' ? '#0f172a' : '#f8fafc', 
              color: msg.role === 'user' ? 'white' : '#1e293b',
              boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
              border: msg.role === 'ai' ? '1px solid #cbd5e1' : 'none',
              boxSizing: 'border-box'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', fontSize: '0.78rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  {msg.role === 'user' ? <User size={14} style={{ marginRight: '6px' }} /> : <Bot size={14} style={{ marginRight: '6px', color: '#0284c7' }} />}
                  {msg.role === 'user' ? 'Advocate / Client Query' : 'Elum Legal AI Dual Response Node'}
                </div>

                {/* AI VOICE AUDIO PLAYBACK BUTTON FOR CLIENT ANSWER */}
                {msg.role === 'ai' && (
                  <button
                    type="button"
                    onClick={() => playAudioAnswer(msg.clientSummary || msg.content, index)}
                    style={{
                      padding: '0.35rem 0.75rem',
                      borderRadius: '20px',
                      backgroundColor: playingMsgIndex === index ? '#dc2626' : '#16a34a',
                      color: 'white',
                      border: 'none',
                      fontSize: '0.75rem',
                      fontWeight: '800',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                    }}
                  >
                    {playingMsgIndex === index ? <VolumeX size={14} /> : <Volume2 size={14} />}
                    {playingMsgIndex === index 
                      ? 'Stop Audio' 
                      : /[\u0600-\u06FF]/.test(msg.clientSummary || msg.content)
                      ? '🔊 Listen Audio Reply (اردو میں سنیں)'
                      : '🔊 Listen Audio Reply (Client Summary)'}
                  </button>
                )}
              </div>

              {/* MANDATORY WELCOME HEADER BANNER IN EVERY AI REPLY */}
              {msg.role === 'ai' && (
                <div style={{ backgroundColor: '#0f172a', color: '#f59e0b', padding: '0.65rem 1rem', borderRadius: '10px', fontWeight: '800', fontSize: '0.88rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid rgba(245,158,11,0.4)', marginBottom: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>⚖️</span> Welcome to Elum Law Associates AI Legal Assistant
                  </span>
                  <span style={{ fontSize: '0.7rem', backgroundColor: '#f59e0b', color: '#0f172a', padding: '0.15rem 0.5rem', borderRadius: '12px', textTransform: 'uppercase', fontWeight: '900' }}>VPS Live AI Node</span>
                </div>
              )}
              {msg.role === 'ai' && (responseMode === 'client_voice' || msg.mode === 'client_voice' || msg.clientSummary) && (
                <div style={{ backgroundColor: '#f0fdf4', padding: '1.15rem', borderRadius: '12px', border: '1px solid #86efac', marginBottom: responseMode === 'client_voice' ? '0' : '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#15803d', fontWeight: '800', fontSize: '0.85rem', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      🗣️ {/[\u0600-\u06FF]/.test(msg.clientSummary || msg.content) ? 'سادہ قانونی خلاصہ - Simple Client Summary (Urdu Response):' : 'Simple Client Summary (Voice Audio Spoken Language):'}
                    </span>
                    <span style={{ fontSize: '0.72rem', backgroundColor: '#dcfce7', color: '#166534', padding: '0.15rem 0.55rem', borderRadius: '12px', border: '1px solid #86efac', fontWeight: '800' }}>
                      {/[\u0600-\u06FF]/.test(msg.clientSummary || msg.content) ? '🎙️ اردو آواز (Urdu Spoken Voice)' : '🎙️ English Spoken Voice'}
                    </span>
                  </div>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '1.02rem', 
                    color: '#166534', 
                    lineHeight: '1.7', 
                    fontWeight: '600',
                    textAlign: /[\u0600-\u06FF]/.test(msg.clientSummary || msg.content) ? 'right' : 'left',
                    direction: /[\u0600-\u06FF]/.test(msg.clientSummary || msg.content) ? 'rtl' : 'ltr'
                  }}>
                    {msg.clientSummary || msg.content}
                  </p>
                </div>
              )}
              
              {/* 2. TECHNICAL EXPERT REPLY BOX FOR ADVOCATES & JUDGES (MODE 1 & MODE 2) */}
              {(msg.role === 'user' || responseMode === 'advisory' || responseMode === 'precedent_rag' || msg.mode === 'advisory' || msg.mode === 'precedent_rag') && (
                <div>
                  {msg.role === 'ai' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#0369a1', fontWeight: '800', fontSize: '0.82rem', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                      🎓 {msg.mode === 'precedent_rag' ? 'Exhaustive Legal Precedent RAG Analysis' : 'Technical Expert Advisory Opinion'} (Elum Law Database & Precedents):
                    </div>
                  )}
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.65', fontSize: '0.95rem' }}>
                    {renderFormattedText(msg.content)}
                  </div>
                </div>
              )}
              
              {/* Interactive Hyperlinked Citations with Instant View Buttons */}
              {msg.role === 'ai' && ((msg.suggestedCases && msg.suggestedCases.length > 0) || (msg.relevantStatutes && msg.relevantStatutes.length > 0)) && (
                <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                  <p style={{ fontSize: '0.82rem', fontWeight: '800', marginBottom: '0.65rem', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    📌 RELEVANT CITATIONS & INSTANT CASE VIEW PREVIEWS:
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                    {msg.suggestedCases?.map((c, i) => {
                      const searchUrl = c.url || `https://elumlaw.com/cases/search?citation=${encodeURIComponent(c.citation || c.title)}&query=${encodeURIComponent(c.title)}`;
                      return (
                        <div 
                          key={i} 
                          style={{ 
                            padding: '0.6rem 0.9rem', 
                            backgroundColor: '#e0f2fe', 
                            borderRadius: '10px', 
                            border: '1px solid #7dd3fc',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.4rem',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            maxWidth: '380px'
                          }}
                        >
                          <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#0369a1' }}>
                            ⚖️ {c.title} ({c.citation})
                          </span>
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.2rem' }}>
                            <button
                              onClick={() => handleInstantView(c.citation, c.title)}
                              style={{ padding: '0.35rem 0.75rem', backgroundColor: '#0284c7', color: 'white', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            >
                              <BookOpen size={13} /> Instant View
                            </button>
                            <a
                              href={searchUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ padding: '0.35rem 0.75rem', backgroundColor: '#0f172a', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.78rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            >
                              <Search size={13} /> Law Directory <ExternalLink size={11} />
                            </a>
                          </div>
                        </div>
                      );
                    })}

                    {msg.relevantStatutes?.map((s, i) => {
                      const searchUrl = s.url || `https://elumlaw.com/cases/search?query=${encodeURIComponent(s.title)}`;
                      return (
                        <div 
                          key={i} 
                          style={{ 
                            padding: '0.6rem 0.9rem', 
                            backgroundColor: '#dcfce7', 
                            borderRadius: '10px', 
                            border: '1px solid #86efac',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.4rem',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            maxWidth: '380px'
                          }}
                        >
                          <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#15803d' }}>
                            📜 {s.type.toUpperCase()}: {s.title}
                          </span>
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.2rem' }}>
                            <button
                              onClick={() => handleInstantView(undefined, s.title)}
                              style={{ padding: '0.35rem 0.75rem', backgroundColor: '#16a34a', color: 'white', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            >
                              <BookOpen size={13} /> Instant View
                            </button>
                            <a
                              href={searchUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ padding: '0.35rem 0.75rem', backgroundColor: '#0f172a', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.78rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            >
                              <Search size={13} /> Search Statute <ExternalLink size={11} />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {msg.disclaimer && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fff5f5', color: '#c53030', fontSize: '0.75rem', borderRadius: '8px', display: 'flex', gap: '8px', border: '1px solid #feb2b2', alignItems: 'center' }}>
                  <AlertCircle size={16} style={{ flexShrink: 0 }} />
                  <span>{msg.disclaimer}</span>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {loading && (
          <div style={{ alignSelf: 'flex-start', color: '#0284c7', fontSize: '0.9rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
            <Bot size={20} className="animate-spin" /> Querying Elum Law Directory, Dictionary, SROs & external Apex precedents...
          </div>
        )}
      </div>

      {/* Input Area with Live Voice Recording Control Bar */}
      <form onSubmit={handleSend} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', backgroundColor: 'white', padding: '0.95rem 1.15rem', borderRadius: '14px', border: responseMode === 'client_voice' ? '2px solid #16a34a' : responseMode === 'precedent_rag' ? '2px solid #0f172a' : '2px solid #0284c7', boxShadow: '0 4px 14px rgba(2,132,199,0.15)', boxSizing: 'border-box' }}>
        
        {/* Active Recording Control Bar */}
        {isListening && (
          <div style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '0.65rem 1rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #fca5a5', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
              <span style={{ width: '12px', height: '12px', backgroundColor: '#dc2626', borderRadius: '50%', display: 'inline-block', boxShadow: '0 0 10px #dc2626' }}></span>
              <span>
                🎙️ {isPaused ? 'Voice Recording Paused' : 'Recording Continuous Audio Stream... (Unlimited Hold-On Time)'}
              </span>
              <span style={{ backgroundColor: '#dc2626', color: 'white', padding: '0.15rem 0.65rem', borderRadius: '12px', fontFamily: 'monospace', fontWeight: '900', fontSize: '0.88rem' }}>
                {Math.floor(recordingSeconds / 60).toString().padStart(2, '0')}:{(recordingSeconds % 60).toString().padStart(2, '0')} / 03:00
              </span>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button 
                type="button" 
                onClick={togglePauseVoiceRecording} 
                style={{ padding: '0.35rem 0.75rem', borderRadius: '6px', border: '1px solid #dc2626', backgroundColor: 'white', color: '#dc2626', fontWeight: '800', cursor: 'pointer', fontSize: '0.78rem' }}
              >
                {isPaused ? '▶️ Resume Recording' : '⏸️ Pause'}
              </button>

              <button 
                type="button" 
                onClick={stopVoiceRecording} 
                style={{ padding: '0.35rem 0.75rem', borderRadius: '6px', border: 'none', backgroundColor: '#dc2626', color: 'white', fontWeight: '800', cursor: 'pointer', fontSize: '0.78rem' }}
              >
                ⏹️ Stop Recording
              </button>

              <button 
                type="button" 
                onClick={handleSend}
                disabled={loading}
                style={{ padding: '0.35rem 0.85rem', borderRadius: '6px', border: 'none', backgroundColor: loading ? '#9ca3af' : '#16a34a', color: 'white', fontWeight: '900', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.3rem', boxShadow: '0 2px 6px rgba(22,163,74,0.3)' }}
              >
                🚀 Submit Voice Query
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          
          {/* VOICE RECORDING MIC BUTTON */}
          <button
            type="button"
            onClick={isListening ? stopVoiceRecording : startVoiceRecording}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: isListening ? '#dc2626' : (responseMode === 'client_voice' ? '#dcfce7' : '#f0f9ff'),
              color: isListening ? 'white' : (responseMode === 'client_voice' ? '#16a34a' : '#0284c7'),
              border: isListening ? '2px solid #b91c1c' : (responseMode === 'client_voice' ? '2px solid #22c55e' : '2px solid #38bdf8'),
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isListening ? '0 0 18px rgba(220,38,38,0.7)' : 'none',
              transition: 'all 0.2s',
              flexShrink: 0
            }}
            title={isListening ? "Stop Voice Recording" : "Click to Start Continuous Voice Recording (Speech Search)"}
          >
            {isListening ? <MicOff size={22} /> : <Mic size={22} />}
          </button>

          {/* SEARCH QUERY TEXT INPUT */}
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              isListening 
                ? "🎙️ Continuous audio recording active... speak in segments, text appears here..." 
                : responseMode === 'client_voice'
                ? "آواز یا متن میں سوال پوچھیں (مثلاً: میرا زمین کا تنازعہ ہے، کیا قانون مجھے تحفظ دیتا ہے؟)"
                : responseMode === 'precedent_rag'
                ? "Search precedents e.g., Supreme Court rulings on civil servant tenure protection (2023 SCMR 456)..."
                : "Ask AI Legal question e.g., grounds for challenging premature transfer under Civil Servants Act..."
            }
            style={{ flex: 1, padding: '0.6rem 0.75rem', border: 'none', outline: 'none', fontSize: '0.98rem', fontFamily: 'inherit' }}
          />

          {/* SUBMIT BUTTON */}
          <button 
            type="submit" 
            disabled={loading || (!query.trim() && !isListening)}
            style={{ 
              padding: '0.75rem 1.4rem', 
              backgroundColor: responseMode === 'client_voice' ? '#16a34a' : responseMode === 'precedent_rag' ? '#0f172a' : '#0284c7', 
              color: 'white', 
              border: 'none', 
              borderRadius: '10px', 
              cursor: loading || (!query.trim() && !isListening) ? 'not-allowed' : 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              fontWeight: '800', 
              fontSize: '0.95rem', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)', 
              opacity: loading || !query.trim() ? 0.6 : 1 
            }}
          >
            <Send size={18} />
            <span>{responseMode === 'client_voice' ? 'Submit Query' : 'Search AI'}</span>
          </button>
        </div>
      </form>

      {/* INSTANT CASE VIEW MODAL OVERLAY IN AI ASSISTANT PANE */}
      {(selectedDoc || modalLoading) && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15,23,42,0.8)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem', boxSizing: 'border-box' }}>
          
          {modalLoading ? (
            <div style={{ backgroundColor: 'white', padding: '3rem', borderRadius: '16px', textAlign: 'center', color: '#0284c7', fontWeight: '800' }}>
              <div style={{ display: 'inline-block', width: '42px', height: '42px', border: '4px solid #e2e8f0', borderTopColor: '#0284c7', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '1rem' }}></div>
              <p>Fetching Instant Case View from Legal Database...</p>
            </div>
          ) : selectedDoc && (
            <div style={{ backgroundColor: 'white', borderRadius: '16px', maxWidth: '950px', width: '100%', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #e2e8f0', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', padding: '2rem', position: 'relative', boxSizing: 'border-box' }}>
              
              <button
                onClick={() => setSelectedDoc(null)}
                style={{ position: 'absolute', top: '1.25rem', right: '1.25rem', background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={20} color="#0f1f2a" />
              </button>

              <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'start', marginBottom: '1.5rem' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '800', backgroundColor: '#0284c7', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '6px' }}>
                    📖 INSTANT CASE VIEW PREVIEW
                  </span>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#0f172a', margin: '0.5rem 0 0.25rem 0' }}>
                    {selectedDoc.citationNumber}
                  </h2>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#0284c7', margin: 0 }}>
                    {selectedDoc.title}
                  </h3>
                </div>

                {/* QR Code in Modal */}
                <div style={{ textAlign: 'center', backgroundColor: '#f8fafc', padding: '0.5rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <img src={generateQRUrl(selectedDoc)} alt="QR Code" style={{ width: '100px', height: '100px', display: 'block', borderRadius: '4px' }} />
                  <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '700' }}>QR Scannable</span>
                </div>
              </div>

              {/* Case Details Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '10px', marginBottom: '1.5rem' }}>
                <div><strong>Court:</strong> {selectedDoc.court || 'Supreme Court of Pakistan'}</div>
                <div><strong>Year:</strong> {selectedDoc.year || 2025}</div>
                <div><strong>Author Judge:</strong> {selectedDoc.metadata?.writing_judge || 'Honorable Bench'}</div>
                <div><strong>Reporter:</strong> {selectedDoc.category || 'Service Law'}</div>
              </div>

              {/* Case History & Executive Summary */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.5rem' }}>Executive Legal Summary & Ratio:</h4>
                <p style={{ backgroundColor: '#f0f9ff', padding: '1rem', borderRadius: '8px', borderLeft: '4px solid #0284c7', lineHeight: '1.6', fontSize: '0.95rem', color: '#1e293b' }}>
                  {selectedDoc.metadata?.conclusion_summary || selectedDoc.summary || selectedDoc.content?.substring(0, 500) || 'Full text available in law directory.'}
                </p>
              </div>

              {/* 5 Key Takeaway Bullets if present */}
              {selectedDoc.metadata?.ai_5_bullets && selectedDoc.metadata.ai_5_bullets.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.5rem' }}>5 Key Legal Takeaways:</h4>
                  <ul style={{ paddingLeft: '1.25rem', lineHeight: '1.6' }}>
                    {selectedDoc.metadata.ai_5_bullets.map((b: string, i: number) => (
                      <li key={i} style={{ marginBottom: '0.35rem', color: '#334155' }}>{b}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Judgment Paragraphs Preview */}
              {selectedDoc.metadata?.judgment_paragraphs && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.5rem' }}>Judgment Paragraphs:</h4>
                  <div style={{ maxHeight: '220px', overflowY: 'auto', backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', lineHeight: '1.6' }}>
                    {selectedDoc.metadata.judgment_paragraphs.slice(0, 8).map((para: string, idx: number) => (
                      <p key={idx} style={{ marginBottom: '0.75rem' }}><strong>[{idx + 1}]</strong> {para}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Modal Bottom Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                <button
                  onClick={() => setSelectedDoc(null)}
                  style={{ padding: '0.6rem 1.25rem', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#64748b', cursor: 'pointer', fontWeight: '600' }}
                >
                  Close Instant View
                </button>

                {selectedDoc.externalUrl && (
                  <a
                    href={selectedDoc.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ padding: '0.6rem 1.25rem', borderRadius: '8px', border: '1px solid #0284c7', backgroundColor: '#e0f2fe', color: '#0369a1', textDecoration: 'none', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                  >
                    Open in Law Directory <ExternalLink size={16} />
                  </a>
                )}

                {selectedDoc._id && (
                  <button
                    onClick={() => triggerDownload(selectedDoc._id)}
                    style={{ padding: '0.6rem 1.5rem', borderRadius: '8px', border: 'none', backgroundColor: '#0284c7', color: 'white', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Download size={18} /> Download Locked Judgment PDF
                  </button>
                )}
              </div>

            </div>
          )}
        </div>
      )}
        </>
      )}

    </div>
  );

  if (isModalView) {
    return (
      <div style={{
        position: 'fixed',
        top: windowState === 'fullscreen' ? 0 : 'auto',
        left: windowState === 'fullscreen' ? 0 : 'auto',
        right: windowState === 'fullscreen' ? 0 : '24px',
        bottom: windowState === 'fullscreen' ? 0 : '24px',
        width: windowState === 'fullscreen' ? '100vw' : `${windowSize.width}px`,
        height: windowState === 'fullscreen' ? '100vh' : `${windowSize.height}px`,
        maxWidth: windowState === 'fullscreen' ? '100vw' : '96vw',
        maxHeight: windowState === 'fullscreen' ? '100vh' : '94vh',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(10px)',
        zIndex: 2500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: windowState === 'fullscreen' ? 0 : '0.5rem',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        <div style={{
          backgroundColor: colors.cardBg,
          color: colors.textColor,
          border: `2px solid ${colors.goldPrimary}`,
          borderRadius: windowState === 'fullscreen' ? '0' : '16px',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0,0,0,0.8), 0 0 30px rgba(79, 70, 229, 0.25)',
          overflow: 'hidden',
          boxSizing: 'border-box',
          position: 'relative'
        }}>
          {content}

          {/* CLICK & DRAG 2D RESIZE HANDLE (HORIZONTAL & VERTICAL) AT BOTTOM-RIGHT CORNER */}
          {windowState !== 'fullscreen' && (
            <div 
              onMouseDown={handleStartResize}
              title="Click and drag horizontally or vertically to resize window size"
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '20px',
                height: '20px',
                cursor: 'nwse-resize',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#4f46e5',
                color: 'white',
                borderRadius: '4px 0 14px 0',
                userSelect: 'none'
              }}
            >
              <span style={{ fontSize: '11px', fontWeight: 'bold', lineHeight: 1 }}>↘</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return content;
};

export default AiAssistant;
